import * as React from 'react';

import { useLockFn, useMemoizedFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useCatalogAction, useCatalogValue } from 'context';
import { createCatalog, updateCatalog } from 'services';
import { PATHNAME } from 'utils';

import { CatalogFormAction } from './action';
import { CatalogFormTabs } from './tabs';

const {
  LOCAL: {
    CATALOGS: { BASE_URL: CATALOGS_BASE_URL },
  },
} = PATHNAME;

type FieldType = Partial<Record<string, string>>;

const CatalogForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    brand,
    description,
    catalogs = [],
    seo,
    defaultTitle,
    mode,
    step,
  } = useCatalogValue();
  const action = useCatalogAction();
  const navigate = useNavigate();

  const handleUpdateCatalogBaseInfo = useMemoizedFn(() => {
    action({
      type: 'UPDATE_CATALOG_STEP',
      payload: {
        step: 2,
      },
    });
  });

  const handleUpdateCatalogFilesInfo = useMemoizedFn((files: FieldType) => {
    action({
      type: 'UPDATE_CATALOG_FILES',
      payload: {
        catalogs: catalogs
          .map(catalog => {
            const { id } = catalog;
            const title = String(files[`file-title-${id}`]);

            return {
              ...catalog,
              title,
            };
          })
          .sort((a, b) => {
            const aIndex = Number(files[`file-index-${a.id}`]);
            const bIndex = Number(files[`file-index-${b.id}`]);

            if (aIndex === bIndex) {
              return catalogs.indexOf(a) - catalogs.indexOf(b);
            }
            return aIndex - bIndex;
          }),
      },
    });
    action({
      type: 'UPDATE_CATALOG_STEP',
      payload: {
        step: 3,
      },
    });
  });

  const handleUpdateCatalogSeoInfo = useLockFn(async (files: FieldType) => {
    const { id: brandId = '', title: brandTitle = '' } = brand ?? {};
    const isUpdatedMode = mode === 'Update';

    const params: Parameters<typeof updateCatalog>[0] = {
      id,
    };
    const body: Parameters<typeof updateCatalog>[1] = {
      description,
      brandId,
      catalogs: catalogs.map(
        ({
          title,
          publishTime,
          banner: { id: bannerId = '' } = {},
          file: { id: fileId },
        }) => ({
          title,
          publishTime,
          bannerId,
          fileId,
        }),
      ),
      seo,
    };

    (isUpdatedMode ? updateCatalog(params, body) : createCatalog(body))
      .then(() => {
        notification.success({
          message: `کاتالوگ ${isUpdatedMode ? defaultTitle : brandTitle} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
        });
        navigate(CATALOGS_BASE_URL);
      })
      .catch(message => {
        notification.error({ message });
      });
  });

  const onFinish = useLockFn(async (fields: FieldType) => {
    switch (step) {
      case 1: {
        return handleUpdateCatalogBaseInfo();
      }
      case 2: {
        return handleUpdateCatalogFilesInfo(fields);
      }
      case 3: {
        return handleUpdateCatalogSeoInfo(fields);
      }
      default:
        break;
    }
  });

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'brand',
          value: brand && {
            key: brand.id,
            label: brand.title,
            value: brand.id,
          },
        },
        ...catalogs.map(({ id }, index) => ({
          name: `file-index-${id}`,
          value: index + 1,
        })),
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:flex-1 *:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:max-w-[70%] xl:last:*:max-w-[30%]'
        gap='large'
      >
        <CatalogFormTabs />
        <CatalogFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { CatalogForm };
